"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const project_config_1 = __importDefault(require("evolution-common/lib/config/project.config"));
const react_i18next_1 = require("react-i18next");
const maplibre_1 = __importStar(require("react-map-gl/maplibre"));
require("maplibre-gl/dist/maplibre-gl.css");
const mapbox_1 = require("@deck.gl/mapbox");
const layers_1 = require("@deck.gl/layers");
const ColorUtils_1 = require("evolution-common/lib/utils/ColorUtils");
const activityIconMapping_1 = require("evolution-common/lib/services/questionnaire/sections/visitedPlaces/activityIconMapping");
const AnimatedArrowPathExtension_1 = __importDefault(require("./AnimatedArrowPathExtension"));
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const faExpand_1 = require("@fortawesome/free-solid-svg-icons/faExpand");
const faLocationDot_1 = require("@fortawesome/free-solid-svg-icons/faLocationDot");
const faRotateLeft_1 = require("@fortawesome/free-solid-svg-icons/faRotateLeft");
const faLayerGroup_1 = require("@fortawesome/free-solid-svg-icons/faLayerGroup");
/*
    TODO: implement clustering for visited places at same location, but with distinct activities
    TODO: when all interview data will be separated in distinct classes/survey objects, we will allow multiple
    usual places, so we will have to update how they are used when selected/click on. Right now,
    the usual place path for study and work is unique, but they will not be in the future.
*/
/**
 * DeckGL Overlay Control Component for Animated Trip Paths
 */
const DeckGLControl = ({ layers, activeTripUuid }) => {
    const overlayRef = (0, react_1.useRef)(null);
    (0, maplibre_1.useControl)(() => {
        overlayRef.current = new mapbox_1.MapboxOverlay({
            interleaved: true,
            _animate: true,
            useDevicePixels: true,
            layers
        });
        return overlayRef.current;
    });
    (0, react_1.useEffect)(() => {
        if (overlayRef.current) {
            overlayRef.current.setProps({ layers });
        }
    }, [layers, activeTripUuid]);
    return null;
};
/**
 * Interview Map Component with marker drag + undo and DeckGL trip overlay
 */
const InterviewMap = (props) => {
    const { t } = (0, react_i18next_1.useTranslation)(['admin']);
    const mapRef = (0, react_1.useRef)(null);
    const layerSelectRef = (0, react_1.useRef)(null);
    const [mapLoaded, setMapLoaded] = (0, react_1.useState)(false);
    const [showAerialTiles, setShowAerialTiles] = (0, react_1.useState)(false);
    const [showLayerSelect, setShowLayerSelect] = (0, react_1.useState)(false);
    const [viewState, setViewState] = (0, react_1.useState)({
        longitude: props.center ? props.center[0] : project_config_1.default.mapDefaultCenter.lon,
        latitude: props.center ? props.center[1] : project_config_1.default.mapDefaultCenter.lat,
        zoom: 13
    });
    // Drag state
    const [draggedMarker, setDraggedMarker] = (0, react_1.useState)(null);
    // Drag history for undo functionality
    const [dragHistory, setDragHistory] = (0, react_1.useState)([]);
    /**
     * Calculate icon size based on zoom level and active state
     * Range: 5px to 120px between zoom 10 and 20
     */
    const getIconSize = (active, zoom) => {
        let baseSize;
        const minSize = 20;
        const maxSize = 120;
        const factor = (zoom - 10) / (20 - 10);
        baseSize = Math.max(Math.min(minSize + (maxSize - minSize) * factor, maxSize), minSize);
        // Active icons are 1.5x larger than inactive ones
        if (active) {
            baseSize = baseSize * 1.5;
        }
        return Math.round(baseSize);
    };
    /**
     * Track whether places layer has been created to trigger deck layer updates
     */
    const [placesLayerReady, setPlacesLayerReady] = (0, react_1.useState)(false);
    /**
     * Clear drag history when interview changes
     */
    (0, react_1.useEffect)(() => {
        setDragHistory([]);
    }, [props.updateCount]);
    /**
     * Handle clicks outside layer select to close it
     */
    (0, react_1.useEffect)(() => {
        const handleClickOutside = (event) => {
            if (layerSelectRef.current && !layerSelectRef.current.contains(event.target)) {
                setShowLayerSelect(false);
            }
        };
        if (showLayerSelect) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showLayerSelect]);
    /**
     * Handle marker drag start
     */
    const onMarkerDragStart = (placePath, coordinates) => {
        // Note: We don't clear drag history here - it persists across multiple drags
        // History is only cleared when interview changes or component unmounts
        setDraggedMarker({
            placePath,
            originalCoordinates: coordinates,
            currentCoordinates: coordinates
        });
    };
    /**
     * Handle marker drag
     */
    const onMarkerDrag = (placePath, coordinates) => {
        if (draggedMarker && draggedMarker.placePath === placePath) {
            setDraggedMarker(Object.assign(Object.assign({}, draggedMarker), { currentCoordinates: coordinates }));
        }
    };
    /**
     * Handle marker drag end
     */
    const onMarkerDragEnd = (placePath, coordinates) => {
        if (draggedMarker) {
            const moved = draggedMarker.originalCoordinates[0] !== coordinates[0] ||
                draggedMarker.originalCoordinates[1] !== coordinates[1];
            if (moved) {
                // Add to drag history
                setDragHistory((prev) => [
                    ...prev,
                    {
                        placePath: draggedMarker.placePath,
                        originalCoordinates: draggedMarker.originalCoordinates,
                        newCoordinates: coordinates,
                        timestamp: Date.now()
                    }
                ]);
                // Call the parent's onPlaceDrag callback to update coordinates
                if (props.onPlaceDrag) {
                    props.onPlaceDrag(placePath, coordinates);
                }
            }
        }
        setDraggedMarker(null);
    };
    /**
     * Handle undo last drag operation
     */
    const onUndoLastDrag = () => {
        if (dragHistory.length > 0 && props.onPlaceDrag) {
            // Get the last drag operation
            const lastDrag = dragHistory[dragHistory.length - 1];
            // Revert to original coordinates
            props.onPlaceDrag(lastDrag.placePath, lastDrag.originalCoordinates);
            // Remove the last operation from history
            setDragHistory((prev) => prev.slice(0, -1));
        }
    };
    /**
     * Get trip color for deck.gl PathLayer
     */
    const getTripColor = (feature) => {
        var _a, _b;
        const personColor = (_a = feature.properties) === null || _a === void 0 ? void 0 : _a.personColor;
        const tripColor = (_b = feature.properties) === null || _b === void 0 ? void 0 : _b.color;
        const colorHex = tripColor || personColor || '#000000';
        const rgbColor = (0, ColorUtils_1.hexToRgb)(colorHex);
        return rgbColor ? [rgbColor[0], rgbColor[1], rgbColor[2], 255] : [255, 0, 0, 255];
    };
    /**
     * Handle map load event
     */
    const onMapLoad = () => {
        setMapLoaded(true);
    };
    /**
     * Signal that markers are ready for deck layers and trigger fitBounds
     */
    (0, react_1.useEffect)(() => {
        if (!mapLoaded)
            return;
        if (!placesLayerReady) {
            setPlacesLayerReady(true);
        }
    }, [mapLoaded, placesLayerReady]);
    /**
     * Auto-fit bounds when trip is selected
     */
    (0, react_1.useEffect)(() => {
        if (mapLoaded && props.activeTripUuid) {
            fitBoundsToActiveTrip();
        }
    }, [mapLoaded, props.activeTripUuid]);
    /**
     * Auto-zoom to place when a visited place is selected from InterviewStats
     */
    (0, react_1.useEffect)(() => {
        if (mapLoaded && props.activePlacePath && props.shouldZoomToPlace) {
            zoomToActivePlace();
        }
    }, [mapLoaded, props.activePlacePath, props.shouldZoomToPlace]);
    /**
     * Auto-fit bounds when map and places are loaded
     */
    (0, react_1.useEffect)(() => {
        if (mapLoaded && placesLayerReady && props.places.features.length > 0) {
            fitBounds();
        }
    }, [mapLoaded, placesLayerReady, props.places.features.length]);
    /**
     * Create deck.gl layers for animated trip paths
     */
    const deckLayers = (0, react_1.useMemo)(() => {
        if (!props.trips.features.length || !mapLoaded || !placesLayerReady)
            return [];
        return [
            new layers_1.PathLayer({
                id: 'trips-animated',
                data: props.trips.features,
                antialias: true,
                highPrecision: true,
                getPath: (feature) => {
                    return feature.geometry.coordinates;
                },
                getColor: (feature) => {
                    return getTripColor(feature);
                },
                getWidth: (feature) => {
                    return feature.properties.active === true ? 12 : 4;
                },
                widthUnits: 'pixels',
                widthMinPixels: 4,
                widthMaxPixels: 12,
                capRounded: true,
                jointRounded: true,
                pickable: true,
                onClick: (info) => {
                    var _a;
                    if (info.object && props.onTripClick) {
                        const tripUuid = (_a = info.object.properties) === null || _a === void 0 ? void 0 : _a.tripUuid;
                        if (tripUuid) {
                            props.onTripClick(tripUuid);
                        }
                    }
                },
                extensions: [new AnimatedArrowPathExtension_1.default()],
                updateTriggers: {
                    getPath: [props.activeTripUuid],
                    getColor: [props.activeTripUuid],
                    getWidth: [props.activeTripUuid]
                }
            })
        ];
    }, [props.trips.features, mapLoaded, placesLayerReady]);
    /**
     * Toggle layer select menu visibility
     */
    const toggleLayerSelect = () => {
        setShowLayerSelect((prev) => !prev);
    };
    /**
     * Handle map layer selection change
     */
    const handleLayerChange = (layerType) => {
        setShowAerialTiles(layerType === 'aerial');
        setShowLayerSelect(false);
    };
    /**
     * Generate map style with tile sources
     */
    const getMapStyle = () => {
        const sources = {
            osm: {
                type: 'raster',
                tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors'
            }
        };
        const layers = [];
        // Add aerial source if configured
        if (project_config_1.default.mapAerialTilesUrl) {
            sources.aerial = {
                type: 'raster',
                tiles: [project_config_1.default.mapAerialTilesUrl],
                tileSize: 256,
                attribution: 'Aerial imagery'
            };
        }
        // Add the appropriate base layer
        if (showAerialTiles && project_config_1.default.mapAerialTilesUrl) {
            layers.push({
                id: 'aerial',
                type: 'raster',
                source: 'aerial'
            });
        }
        else {
            layers.push({
                id: 'osm',
                type: 'raster',
                source: 'osm'
            });
        }
        return {
            version: 8, // must be 8, see https://maplibre.org/maplibre-style-spec/root/#version
            sources,
            layers
        };
    };
    /**
     * Fit map bounds to show all places and trips
     */
    const fitBounds = () => {
        var _a;
        const map = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
        if (!map)
            return;
        const allCoordinates = [];
        // Add place coordinates
        props.places.features.forEach((feature) => {
            if (feature.geometry.coordinates) {
                allCoordinates.push(feature.geometry.coordinates);
            }
        });
        // Add trip coordinates
        props.trips.features.forEach((feature) => {
            feature.geometry.coordinates.forEach((coord) => {
                allCoordinates.push(coord);
            });
        });
        if (allCoordinates.length > 0) {
            const lngs = allCoordinates.map((coord) => coord[0]);
            const lats = allCoordinates.map((coord) => coord[1]);
            const bounds = [
                [Math.min(...lngs), Math.min(...lats)],
                [Math.max(...lngs), Math.max(...lats)]
            ];
            map.fitBounds(bounds, { padding: 50, animate: false, maxZoom: 17 });
        }
    };
    /**
     * Fit map bounds to show the active trip's origin and destination
     */
    const fitBoundsToActiveTrip = () => {
        var _a;
        const map = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
        if (!map || !props.activeTripUuid)
            return;
        // Find the active trip
        const activeTrip = props.trips.features.find((feature) => { var _a; return ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.tripUuid) === props.activeTripUuid; });
        if (!activeTrip || !activeTrip.geometry.coordinates.length)
            return;
        const coordinates = activeTrip.geometry.coordinates;
        // Get origin (first coordinate) and destination (last coordinate)
        const origin = coordinates[0];
        const destination = coordinates[coordinates.length - 1];
        // Create bounds from origin and destination
        const lngs = [origin[0], destination[0]];
        const lats = [origin[1], destination[1]];
        const bounds = [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)]
        ];
        // Add some padding and animate to the bounds
        map.fitBounds(bounds, {
            padding: 100, // More padding for better view of origin/destination
            animate: true,
            duration: 1000, // Smooth animation
            maxZoom: 17 // Limit zoom to prevent blurry tiles
        });
    };
    /**
     * Zoom to active visited place at zoom level 16
     */
    const zoomToActivePlace = () => {
        var _a;
        const map = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
        if (!map || !props.activePlacePath)
            return;
        // Find the active place
        const activePlace = props.places.features.find((feature) => { var _a; return ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.path) === props.activePlacePath; });
        if (!activePlace || !activePlace.geometry.coordinates)
            return;
        const coordinates = activePlace.geometry.coordinates;
        // Smoothly animate to the place with zoom level 14
        map.flyTo({
            center: coordinates,
            zoom: 14,
            duration: 500 // 0.5 second animation
        });
    };
    const mapStyle = (0, react_1.useMemo)(getMapStyle, [showAerialTiles, project_config_1.default.mapAerialTilesUrl]);
    return (react_1.default.createElement("div", { className: "admin__interview-map", style: { height: 'calc(100vh - 120px)', width: '100%' } },
        react_1.default.createElement(maplibre_1.default, Object.assign({ canvasContextAttributes: {
                antialias: true
            }, ref: mapRef }, viewState, { onMove: (evt) => setViewState(evt.viewState), onLoad: onMapLoad, style: { width: '100%', height: '100%' }, maxZoom: 20, mapStyle: mapStyle }),
            props.places.features.map((feature, index) => {
                var _a, _b, _c;
                const coordinates = feature.geometry.coordinates;
                const activity = (_a = feature.properties) === null || _a === void 0 ? void 0 : _a.activity;
                const active = (_b = feature.properties) === null || _b === void 0 ? void 0 : _b.active;
                const placePath = (_c = feature.properties) === null || _c === void 0 ? void 0 : _c.path;
                const iconPath = (0, activityIconMapping_1.getActivityMarkerIcon)(activity);
                if (!iconPath)
                    return null;
                const iconSize = getIconSize(active || false, viewState.zoom);
                // Use dragged coordinates if this marker is being dragged
                const displayCoordinates = draggedMarker && draggedMarker.placePath === placePath
                    ? draggedMarker.currentCoordinates
                    : coordinates;
                return (react_1.default.createElement(maplibre_1.Marker, { key: `place-marker-${index}-${placePath}-${viewState.zoom}`, longitude: displayCoordinates[0], latitude: displayCoordinates[1], anchor: "bottom", offset: [0, 4], draggable: true, onDragStart: () => placePath && onMarkerDragStart(placePath, coordinates), onDrag: (e) => placePath && onMarkerDrag(placePath, [e.lngLat.lng, e.lngLat.lat]), onDragEnd: (e) => placePath && onMarkerDragEnd(placePath, [e.lngLat.lng, e.lngLat.lat]), onClick: (e) => {
                        e.originalEvent.stopPropagation();
                        if (placePath && props.onPlaceClick) {
                            props.onPlaceClick(placePath);
                        }
                    } },
                    react_1.default.createElement("img", { src: iconPath, alt: activity, className: `admin__map-marker ${draggedMarker && draggedMarker.placePath === placePath ? 'dragging' : ''}`, style: {
                            width: `${iconSize}px`,
                            height: `${iconSize}px`,
                            opacity: draggedMarker && draggedMarker.placePath === placePath ? 0.7 : 1
                        } })));
            }),
            react_1.default.createElement(DeckGLControl, { layers: deckLayers, activeTripUuid: props.activeTripUuid })),
        react_1.default.createElement("div", { className: "admin__interview-map-controls" },
            project_config_1.default.mapAerialTilesUrl && (react_1.default.createElement("div", { ref: layerSelectRef, className: "admin__map-layer-switcher" },
                react_1.default.createElement("button", { type: "button" // disable defaut form submission (default is type=submit)
                    , onClick: toggleLayerSelect, className: "admin__map-layer-button", title: t('admin:MapLayerSelect') },
                    react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faLayerGroup_1.faLayerGroup })),
                showLayerSelect && (react_1.default.createElement("div", { className: "admin__map-layer-dropdown" },
                    react_1.default.createElement("div", { onClick: () => handleLayerChange('osm'), className: `admin__map-layer-option ${!showAerialTiles ? 'active' : ''}` },
                        react_1.default.createElement("img", { src: "https://raw.githubusercontent.com/muimsd/map-gl-style-switcher/refs/heads/main/public/osm.png", alt: "OSM", className: `admin__map-layer-thumbnail ${!showAerialTiles ? 'active' : ''}` }),
                        react_1.default.createElement("span", { className: `admin__map-layer-label ${!showAerialTiles ? 'active' : ''}` }, t('admin:MapLayerOSM'))),
                    react_1.default.createElement("div", { onClick: () => handleLayerChange('aerial'), className: `admin__map-layer-option ${showAerialTiles ? 'active' : ''}` },
                        react_1.default.createElement("div", { className: `admin__map-layer-thumbnail admin__map-layer-aerial-thumbnail ${showAerialTiles ? 'active' : ''}` }),
                        react_1.default.createElement("span", { className: `admin__map-layer-label ${showAerialTiles ? 'active' : ''}` }, t('admin:MapLayerAerial'))))))),
            dragHistory.length > 0 && (react_1.default.createElement("button", { type: "button" // disable defaut form submission (default is type=submit)
                , onClick: onUndoLastDrag, className: "admin__map-undo-button", title: t('admin:UndoDrag', `Undo last drag operation (${dragHistory.length} operation${dragHistory.length !== 1 ? 's' : ''} to undo)`) },
                react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faRotateLeft_1.faRotateLeft }),
                dragHistory.length > 1 && react_1.default.createElement("span", { className: "admin__map-undo-count" }, dragHistory.length))),
            react_1.default.createElement("button", { type: "button" // disable defaut form submission (default is type=submit)
                , onClick: fitBounds, className: "admin__map-fitbounds-button", title: t('FitBounds') },
                react_1.default.createElement("div", { className: "admin__map-fitbounds-icon" },
                    react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faExpand_1.faExpand, className: "fa-expand" }),
                    react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faLocationDot_1.faLocationDot, className: "fa-location-dot" }))))));
};
exports.default = InterviewMap;
//# sourceMappingURL=InterviewMap.js.map